var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = value => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = value => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = x => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/react/context-providers/SegmentationProvider.tsx
import React, { useMemo, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuthentication } from "@printdeal/authentication";
import { useQueryClient } from "@tanstack/react-query";

// src/react/helpers/window.ts
var windowPresent = typeof window !== "undefined";
var getFromLocalStorage = key => {
  if (!windowPresent) {
    return void 0;
  }
  return window.localStorage.getItem(key);
};
var setInLocalStorage = (key, value) => {
  if (windowPresent) {
    window.localStorage.setItem(key, value);
  }
};

// src/react/queries/useGetTraitsQuery.ts
import { useQuery } from "@tanstack/react-query";

// src/react/http-clients/SegmentationHttpClient.ts
import axios from "axios";
import { configurationProvider } from "@printdeal/configuration";
import { authenticateHttpRequestInterceptor } from "@printdeal/authentication";
var SegmentationHttpClient = class {
  client(token) {
    if (!this.httpClient) {
      const baseURL = configurationProvider.getValue("SEGMENTATION_API_BASE_URL");
      this.httpClient = axios.create({
        baseURL,
        headers: {
          "Authorization": "Bearer ".concat(token),
          "Accept-Encoding": "gzip, deflate, br",
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      this.httpClient.interceptors.request.use(authenticateHttpRequestInterceptor);
    }
    return this.httpClient;
  }
  getCustomerData(token) {
    return __async(this, null, function* () {
      if (!token) {
        throw new Error("User not loggedin");
      }
      const {
        data
      } = yield this.client(token).get("/customerProgramData");
      if (data.errors) {
        throw new Error("Unauthorised token");
      }
      return data.data.customer;
    });
  }
};
var segmentationHttpClient = new SegmentationHttpClient();
var SegmentationHttpClient_default = segmentationHttpClient;

// src/react/types/index.ts
var SEGMENT_TRAITS = "segmentTraits";
var SEGMENTATION_DATA = "segmentationData";

// src/react/queries/useGetTraitsQuery.ts
function useGetTraitsQuery(_ref) {
  let {
    customerId,
    authToken
  } = _ref;
  return useQuery({
    queryKey: ["traits", customerId],
    queryFn: () => __async(null, null, function* () {
      const data = yield SegmentationHttpClient_default.getCustomerData(authToken);
      setInLocalStorage(SEGMENT_TRAITS, JSON.stringify(data.traits));
      setInLocalStorage(SEGMENTATION_DATA, JSON.stringify(data));
      return data;
    }),
    retry: false,
    enabled: Boolean(customerId)
  });
}

// src/react/context-providers/SegmentationContext.ts
import { createContext } from "react";
var SegmentationContext = createContext({
  status: "pending",
  isEnabled: false,
  isPending: true,
  isSuccess: false,
  isError: false
});

// src/react/context-providers/SegmentationProvider.tsx
var SegmentationProvider = _ref2 => {
  let {
    children
  } = _ref2;
  var _a;
  const authenticationService = useAuthentication();
  const queryClient = useQueryClient();
  useEffect(() => {
    const unsubscribeFromLogout = authenticationService.onLogout(() => {
      queryClient.invalidateQueries({
        queryKey: ["traits"]
      });
    });
    return () => {
      unsubscribeFromLogout();
    };
  }, []);
  const authToken = getFromLocalStorage("authToken");
  const decodedToken = authToken ? jwtDecode(authToken) : void 0;
  const getTraitsQuery = useGetTraitsQuery({
    customerId: (_a = decodedToken == null ? void 0 : decodedToken.customer) == null ? void 0 : _a.id,
    authToken
  });
  const contextValue = useMemo(() => {
    var _a2;
    return {
      status: getTraitsQuery.status,
      isEnabled: Boolean((_a2 = decodedToken == null ? void 0 : decodedToken.customer) == null ? void 0 : _a2.id),
      isPending: getTraitsQuery.isPending,
      isSuccess: getTraitsQuery.isSuccess,
      isError: getTraitsQuery.isError,
      traits: getTraitsQuery.data,
      data: getTraitsQuery.data
    };
  }, [getTraitsQuery.status, getTraitsQuery.isPending, getTraitsQuery.isSuccess, getTraitsQuery.isError, getTraitsQuery.data]);
  return /* @__PURE__ */React.createElement(SegmentationContext.Provider, {
    value: contextValue
  }, children);
};
var SegmentationProvider_default = SegmentationProvider;

// src/react/SegmentVariants/SegmentVariants.tsx
import React3 from "react";

// src/react/hooks/useTraits.ts
import { useContext } from "react";
function useTraits() {
  const context = useContext(SegmentationContext);
  return context.data;
}

// src/react/hooks/use-segmentation.ts
import { useContext as useContext2 } from "react";
var useSegmentation = () => {
  const context = useContext2(SegmentationContext);
  return context;
};
var use_segmentation_default = useSegmentation;

// src/react/helpers/InvoiceCollectiveHelper.ts
var RESELLER_LEVEL_FREQUENCIES = {
  basic: ["daily", "weekly"],
  plus: ["daily", "weekly"],
  pro: ["daily", "weekly", "biweekly"],
  premium: ["daily", "weekly", "biweekly", "monthly"],
  excellent: ["daily", "weekly", "biweekly", "monthly"]
};
var ENDUSER_GROUP_FREQUENCIES = {
  // we are excluding NOT_CATEGORIZED group
  "Group 1": ["daily", "weekly"],
  "Group 2": ["daily", "weekly", "biweekly"],
  "Group 3": ["daily", "weekly", "biweekly"],
  "Group 4": ["daily", "weekly", "biweekly"],
  "Group 5": ["daily", "weekly", "biweekly", "monthly"],
  "Group 6": ["daily", "weekly", "biweekly", "monthly"],
  "Group 7": ["daily", "weekly", "biweekly", "monthly"]
};
var DEFAULT_FREQUENCIES = [];

// src/react/hooks/useInvoiceCollectiveFrequency.ts
function useInvoiceCollectiveFrequency() {
  var _a, _b;
  const {
    data: segmentationData
  } = use_segmentation_default();
  if (!segmentationData) return DEFAULT_FREQUENCIES;
  const {
    isReseller: isReseller2,
    category,
    traits
  } = segmentationData;
  if (isReseller2 && category) {
    const level = category.toLowerCase();
    return (_a = RESELLER_LEVEL_FREQUENCIES[level]) != null ? _a : DEFAULT_FREQUENCIES;
  }
  const groupName = category && category !== "EndusersToBeChecked" ? category : traits.companiesCategory;
  return groupName ? (_b = ENDUSER_GROUP_FREQUENCIES[groupName]) != null ? _b : DEFAULT_FREQUENCIES : DEFAULT_FREQUENCIES;
}

// src/react/SegmentVariants/SegmentVariantsProvider.tsx
import React2, { useMemo as useMemo2, useState } from "react";

// src/react/helpers/segmentation-filter.ts
var ValidationFailureReasons = {
  OtherFilterMatched: "Other filter matched",
  NotAReseller: "The user is not a reseller.",
  NotAnEndUser: "The user is not an end-user.",
  NotInCustomerProgram: "The user is not in any customer program.",
  AudienceWhitelistFailure: "AudienceWhitelistFailure",
  AudienceBlacklistFailure: "The user is in one or more of the invalid audiences.",
  CategoryWhitelistFailure: "CategoryWhitelistFailure",
  CategoryBlacklistFailure: "The user is in one of the invalid categories.",
  LifetimeOrdersMissing: "Lifetime orders data is missing.",
  LifetimeOrderMinimumFailure: "Lifetime orders is too low.",
  LifetimeOrderMaximumFailure: "Lifetime orders is too high.",
  Revenue365Missing: "Revenue365 is missing.",
  Revenue365MinimumFailure: "Revenue365 is too low.",
  Revenue365MaximumFailure: "Revenue365 is too high."
};
var isEndUser = segmentationData => segmentationData.isEnduser;
var isReseller = segmentationData => segmentationData.isReseller;
var isInCustomerProgram = segmentationData => segmentationData.isPartOfCustomerProgram;
var isAudiencesValid = (segmentationData, targetAudiences, matchType) => {
  if (targetAudiences.length === 0) {
    return true;
  }
  if (segmentationData.audiences.length === 0 && matchType !== "none") {
    return false;
  }
  const audienceExists = audience => segmentationData.audiences.indexOf(audience) !== -1;
  switch (matchType) {
    case "all":
      return targetAudiences.every(audienceExists);
    case "any":
      return targetAudiences.some(audienceExists);
    case "none":
      return !targetAudiences.some(audienceExists);
    default:
      return false;
  }
};
var isCategoryValid = (segmentationData, targetCategories, matchType) => {
  const isInTargetCategory = targetCategories.indexOf(segmentationData.category) !== -1;
  return matchType === "any" ? isInTargetCategory : !isInTargetCategory;
};
var isTraitLimitValid = function (segmentationData, traitKey, limit, comparison) {
  let allowEmpty = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  const traitValue = segmentationData.traits[traitKey];
  if (!traitValue || typeof traitValue !== "number") {
    return allowEmpty;
  }
  return comparison === "min" ? traitValue >= limit : traitValue <= limit;
};
var isTraitMissingWhileRequired = function (segmentationData, traitKey) {
  let allowEmpty = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  return !(allowEmpty || segmentationData.traits[traitKey]);
};
var yieldFailure = reason => ({
  success: false,
  reason
});
var validateCustomerSegment = function (segmentationData) {
  let filters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (typeof filters.isReseller === "boolean") {
    const outcome = isReseller(segmentationData);
    if (outcome !== filters.isReseller) {
      return yieldFailure(ValidationFailureReasons.NotAReseller);
    }
  }
  if (typeof filters.isEndUser === "boolean") {
    const outcome = isEndUser(segmentationData);
    if (outcome !== filters.isEndUser) {
      return yieldFailure(ValidationFailureReasons.NotAnEndUser);
    }
  }
  if (typeof filters.isInCustomerProgram === "boolean") {
    const outcome = isInCustomerProgram(segmentationData);
    if (outcome !== filters.isInCustomerProgram) {
      return yieldFailure(ValidationFailureReasons.NotInCustomerProgram);
    }
  }
  if (filters.audienceWhitelist && filters.audienceWhitelist.audiences.length > 0 && !isAudiencesValid(segmentationData, filters.audienceWhitelist.audiences, filters.audienceWhitelist.matchType)) {
    return yieldFailure(ValidationFailureReasons.AudienceWhitelistFailure);
  }
  if (filters.audienceBlacklist && filters.audienceBlacklist.audiences.length > 0 && !isAudiencesValid(segmentationData, filters.audienceBlacklist.audiences, "none")) {
    return yieldFailure(ValidationFailureReasons.AudienceBlacklistFailure);
  }
  if (filters.categoryWhitelist && filters.categoryWhitelist.length > 0 && !isCategoryValid(segmentationData, filters.categoryWhitelist, "any")) {
    return yieldFailure(ValidationFailureReasons.CategoryWhitelistFailure);
  }
  if (filters.categoryBlacklist && filters.categoryBlacklist.length > 0 && !isCategoryValid(segmentationData, filters.categoryBlacklist, "none")) {
    return yieldFailure(ValidationFailureReasons.CategoryBlacklistFailure);
  }
  if (filters.ordersLifetime) {
    const {
      min,
      max,
      allowEmpty
    } = filters.ordersLifetime;
    if (isTraitMissingWhileRequired(segmentationData, "orders_lifetime", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.LifetimeOrdersMissing);
    }
    if (min && !isTraitLimitValid(segmentationData, "orders_lifetime", min, "min", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.LifetimeOrderMinimumFailure);
    }
    if (max && !isTraitLimitValid(segmentationData, "orders_lifetime", max, "max", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.LifetimeOrderMaximumFailure);
    }
  }
  if (filters.revenue365) {
    const {
      min,
      max,
      allowEmpty
    } = filters.revenue365;
    if (isTraitMissingWhileRequired(segmentationData, "revenue_365", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.Revenue365Missing);
    }
    if (min && !isTraitLimitValid(segmentationData, "revenue_365", min, "min", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.Revenue365MinimumFailure);
    }
    if (max && !isTraitLimitValid(segmentationData, "revenue_365", max, "max", allowEmpty)) {
      return yieldFailure(ValidationFailureReasons.Revenue365MaximumFailure);
    }
  }
  return {
    success: true
  };
};

// src/react/SegmentVariants/SegmentVariantsContext.tsx
import { createContext as createContext2 } from "react";
var defaultValue = {
  validationResults: {}
};
var SegmentVariantsContext = createContext2(defaultValue);
var SegmentVariantsContext_default = SegmentVariantsContext;

// src/react/SegmentVariants/SegmentVariantsProvider.tsx
var SegmentVariantsProvider = _ref3 => {
  let {
    children,
    variantFilters,
    fallback = null
  } = _ref3;
  const {
    data: segmentationData
  } = use_segmentation_default();
  const [hasMatch, setHasMatch] = useState(false);
  const outcome = useMemo2(() => {
    let matchFound = false;
    const resultMap = {};
    variantFilters.forEach(variant => {
      if (matchFound) {
        resultMap[variant.id] = {
          success: false,
          reason: ValidationFailureReasons.OtherFilterMatched
        };
      } else {
        const result = validateCustomerSegment(segmentationData, variant.filters);
        resultMap[variant.id] = result;
        if (result.success) {
          matchFound = true;
        }
      }
    });
    setHasMatch(matchFound);
    return {
      validationResults: resultMap
    };
  }, [variantFilters, segmentationData]);
  if (!hasMatch) {
    return fallback;
  }
  return /* @__PURE__ */React2.createElement(SegmentVariantsContext_default.Provider, {
    value: outcome
  }, children);
};
var SegmentVariantsProvider_default = SegmentVariantsProvider;

// src/react/SegmentVariants/SegmentVariants.tsx
var SegmentVariants = _ref4 => {
  let {
    children,
    variantFilters,
    fallback = null,
    disabledView
  } = _ref4;
  const {
    data: segmentationData,
    isEnabled,
    isPending,
    isError
  } = use_segmentation_default();
  if (!isEnabled) {
    return disabledView || fallback;
  }
  if (isPending || isError || !segmentationData) {
    return fallback;
  }
  return /* @__PURE__ */React3.createElement(SegmentVariantsProvider_default, {
    variantFilters,
    fallback
  }, children);
};
var SegmentVariants_default = SegmentVariants;

// src/react/SegmentVariants/SegmentVariant.tsx
import { useContext as useContext3 } from "react";
var SegmentVariant = _ref5 => {
  let {
    id,
    children
  } = _ref5;
  var _a;
  const {
    validationResults
  } = useContext3(SegmentVariantsContext_default);
  if (!((_a = validationResults[id]) == null ? void 0 : _a.success)) {
    return null;
  }
  return children;
};
var SegmentVariant_default = SegmentVariant;

// src/react/helpers/CustomerProgramHelper.ts
var resellerCustomerGroupNames = ["Basic", "Plus", "Premium"];
var endUserCustomerGroupNames = ["Gold", "Silver", "Bronze"];
var resellersToBeChecked = "Resellers To Be Checked";
var company = "Company";
var isCustomerProgramReseller = (isReseller2, isPartOfCustomerProgram) => Boolean(isReseller2 && isPartOfCustomerProgram);
var isCustomerProgramEnduser = (isEnduser, isPartOfCustomerProgram) => Boolean(isEnduser && isPartOfCustomerProgram);
var isResellerToBeChecked = segmentationData => segmentationData.category === resellersToBeChecked;
var isValidForRequestingQuote = segmentationData => segmentationData.isReseller || segmentationData.isEnduser && segmentationData.traits.customerSegmentLevel1 === company;
export { SegmentVariant_default as SegmentVariant, SegmentVariants_default as SegmentVariants, SegmentationContext, SegmentationProvider_default as SegmentationProvider, ValidationFailureReasons, endUserCustomerGroupNames, isAudiencesValid, isCategoryValid, isCustomerProgramEnduser, isCustomerProgramReseller, isEndUser, isInCustomerProgram, isReseller, isResellerToBeChecked, isTraitLimitValid, isTraitMissingWhileRequired, isValidForRequestingQuote, resellerCustomerGroupNames, useInvoiceCollectiveFrequency, use_segmentation_default as useSegmentation, useTraits, validateCustomerSegment };